import React, { useState, useEffect, useRef } from "react";
import SuccessNotification from "../components/SuccessNotification.jsx";
import { useLocation } from "react-router-dom";
import SplashScreen from "../components/SplashScreen.jsx";
import PlayerActivationScreen from "../components/PlayerActivationScreen.jsx";
import MediaDisplay from "../components/MediaDisplay";
import { supabase } from "../lib/supabase";
import { generateDeviceCode, formatDeviceCode } from "../utils/deviceCodeUtils";
import { useMediaNavigation } from "../hooks/useMediaNavigation";
import { usePlayer } from "../hooks/usePlayer";
import { useMediaCache } from '../utils/useMediaCache';
import { getCacheStats, clearMediaCache, getAllMediasFromCache } from '../utils/mediaCache';
import { isYouTubeUrl } from '../utils/youtube';
import WeatherOverlay from '../components/WeatherOverlay';
import ShutdownOverlay from '../components/player/ShutdownOverlay';
import { useRemoteCommands } from '../hooks/useRemoteCommands';
import { useNativeBridge } from '../hooks/useNativeBridge';

// Função de refresh principal (robusta)
function executeRefresh() {
  try {
    window.location.reload();
  } catch (e) {
    window.location.href = window.location.href.split('?')[0] + '?dbrefresh=' + Date.now();
  }
}

// Monitor de banco robusto (polling + Supabase Realtime + interceptação de requests)
function useDatabaseMonitor({ screenId, playerIsActivated }) {
  const refreshState = useRef({
    inProgress: false,
    lastCheck: null,
    mediaData: new Map(),
    channel: null,
    pollingInterval: null,
    lastRefreshTimestamp: 0
  });

  // Função de refresh garantida com proteção contra loops
  function forceRefresh() {
    const now = Date.now();
    // Prevenir múltiplos refreshes em um curto período (5 segundos)
    if (now - refreshState.current.lastRefreshTimestamp < 5000) {
      return;
    }

    refreshState.current.lastRefreshTimestamp = now;

    try {
      // Salvar estado atual
      const currentState = JSON.stringify({
        screenId,
        playerIsActivated,
        mediaData: Array.from(refreshState.current.mediaData.entries()),
        timestamp: now
      });
      sessionStorage.setItem('monitorState', currentState);

      // Tentar reload normal primeiro
      window.location.reload();

      // Fallback com timeout
      setTimeout(() => {
        const fallbackUrl = new URL(window.location.href);
        fallbackUrl.searchParams.set('refresh', now.toString());
        window.location.replace(fallbackUrl.toString());
      }, 1000);
    } catch (e) {
      // Último recurso: reload básico
      window.location.reload();
    }
  }

  // Função principal de trigger com debounce
  const triggerRefresh = React.useCallback((reason, mediaId = null, newTime = null) => {
    if (refreshState.current.inProgress) {
      return;
    }

    refreshState.current.inProgress = true;

    if (mediaId && newTime) {
      const changeData = {
        mediaId,
        newTime,
        showNotification: true,
        timestamp: Date.now(),
        screenId: screenId // Adicionar screenId para validação
      };
      localStorage.setItem('displayTimeChanged', JSON.stringify(changeData));
    }

    // Executar o refresh após salvar o estado
    setTimeout(() => {
      forceRefresh();
    }, 500);
  }, [screenId]);

  // Polling do banco otimizado
  useEffect(() => {
    if (!playerIsActivated || !screenId) {
      return;
    }

    // Limpar intervalo existente
    if (refreshState.current.pollingInterval) {
      clearInterval(refreshState.current.pollingInterval);
      refreshState.current.pollingInterval = null;
    }

    // Recuperar estado anterior se existir
    try {
      const savedState = sessionStorage.getItem('monitorState');
      if (savedState) {
        const parsed = JSON.parse(savedState);
        if (parsed.screenId === screenId) {
          refreshState.current.mediaData = new Map(parsed.mediaData);
          console.log('📋 Estado anterior restaurado');
        }
        sessionStorage.removeItem('monitorState');
      }
    } catch (e) {
      // Ignorar erro
    } async function checkDatabaseChanges() {
      if (refreshState.current.inProgress) {
        return;
      } try {
        const { data: currentData, error } = await supabase
          .from('medias')
          .select('id,display_time,selected_screens,title')
          .eq('active', true)
          .contains('selected_screens', [screenId]);

        if (error) throw error;

        if (!Array.isArray(currentData)) {
          return;
        }

        let hasChanges = false;
        const changedMedias = [];

        currentData.forEach(media => {
          const key = media.id;
          const lastData = refreshState.current.mediaData.get(key);

          if (!lastData) {
            hasChanges = true;
            changedMedias.push(media);
          } else {
            if (lastData.display_time !== media.display_time) {
              hasChanges = true;
              changedMedias.push(media);
            }
            if (JSON.stringify(lastData.selected_screens) !== JSON.stringify(media.selected_screens)) {
              hasChanges = true;
              changedMedias.push(media);
            }
          }

          refreshState.current.mediaData.set(key, {
            display_time: media.display_time,
            selected_screens: media.selected_screens,
            title: media.title
          });
        });

        // Verificar mídias removidas
        const currentIds = new Set(currentData.map(m => m.id));
        for (const [key, value] of refreshState.current.mediaData.entries()) {
          if (!currentIds.has(key)) {
            console.log(`🗑️ Mídia removida: ${value.title || key}`);
            refreshState.current.mediaData.delete(key);
            hasChanges = true;
          }
        }

        if (hasChanges) {
          const firstChanged = changedMedias[0];

          // Usar Promise.resolve().then para garantir que os logs sejam vistos
          Promise.resolve().then(() => {
            triggerRefresh(
              'Polling: Mudanças detectadas',
              firstChanged?.id,
              firstChanged?.display_time
            );
          });
        }

      } catch (error) {
        // Ignorar erro de verificação
      }
    }

    // Intervalo mais frequente (10 segundos)
    refreshState.current.pollingInterval = setInterval(checkDatabaseChanges, 10000);

    // Primeira verificação imediata
    checkDatabaseChanges();

    return () => {
      if (refreshState.current.pollingInterval) {
        clearInterval(refreshState.current.pollingInterval);
        refreshState.current.pollingInterval = null;
      }
    };
  }, [playerIsActivated, screenId, triggerRefresh]);

  // Supabase Realtime com reconexão automática
  useEffect(() => {
    if (!playerIsActivated || !screenId) {
      return;
    }

    // Função para criar e configurar o canal
    function setupChannel() {
      if (refreshState.current.channel) {
        supabase.removeChannel(refreshState.current.channel);
      }

      const channelId = `media-changes-${screenId}-${Date.now()}`;
      const channel = supabase
        .channel(channelId)
        .on(
          'postgres_changes',
          {
            event: '*', // Monitorar todos os eventos
            schema: 'public',
            table: 'medias',
            filter: `selected_screens=cs.{${screenId}}` // Filtro mais eficiente
          },
          (payload) => {
            const { old: oldRecord, new: newRecord } = payload;

            // Ignorar se não tiver os dados necessários
            if (!newRecord || !oldRecord) return;

            // Verificar se esta tela está selecionada
            const isScreenSelected = Array.isArray(newRecord.selected_screens) &&
              newRecord.selected_screens.includes(screenId);

            // Detectar diferentes tipos de mudanças
            const changes = [];

            if (oldRecord.display_time !== newRecord.display_time) {
              changes.push(`tempo:${oldRecord.display_time}→${newRecord.display_time}`);
            }

            if (JSON.stringify(oldRecord.selected_screens) !== JSON.stringify(newRecord.selected_screens)) {
              changes.push('telas');
            }

            if (oldRecord.active !== newRecord.active) {
              changes.push(`status:${oldRecord.active}→${newRecord.active}`);
            }

            if (changes.length > 0 && isScreenSelected) {
              // Atualizar cache local
              refreshState.current.mediaData.set(newRecord.id, {
                display_time: newRecord.display_time,
                selected_screens: newRecord.selected_screens,
                title: newRecord.title
              });

              // Trigger do refresh com detalhes
              triggerRefresh(
                `Realtime: ${changes.join(', ')}`,
                newRecord.id,
                newRecord.display_time
              );
            }
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            // Canal conectado
          }
        });

      refreshState.current.channel = channel;
    }

    // Configuração inicial
    setupChannel();

    // Reconexão automática a cada 5 minutos
    const reconnectInterval = setInterval(() => {
      setupChannel();
    }, 300000);

    return () => {
      clearInterval(reconnectInterval);
      if (refreshState.current.channel) {
        supabase.removeChannel(refreshState.current.channel);
      }
    };
  }, [playerIsActivated, screenId, triggerRefresh]);

  // Interceptação de requests otimizada
  useEffect(() => {
    if (!playerIsActivated || !screenId) return;

    const originalFetch = window.fetch;
    let lastUpdateTime = 0;
    const DEBOUNCE_TIME = 2000; // 2 segundos de debounce

    window.fetch = async function (...args) {
      const response = await originalFetch.apply(this, args);

      try {
        // Só processa se for uma requisição relevante
        if (args[0] && typeof args[0] === 'string') {
          const url = args[0].toLowerCase();
          const isMediaRequest = url.includes('/api/medias') || url.includes('medias');

          if (isMediaRequest) {
            const method = args[1]?.method?.toUpperCase() || 'GET';
            const now = Date.now();

            // Clone e tenta parsear a resposta
            const responseClone = response.clone();
            const data = await responseClone.json();

            console.log(`🌐 Request interceptado: ${method} ${url}`);

            // Verificar mudanças relevantes para esta tela
            if (['PUT', 'PATCH', 'POST'].includes(method)) {
              // Evitar múltiplos refreshes em sequência
              if (now - lastUpdateTime > DEBOUNCE_TIME) {
                lastUpdateTime = now;

                // Verificar se a mídia é relevante para esta tela
                if (data && Array.isArray(data.selected_screens) && data.selected_screens.includes(screenId)) {
                  // Atualizar cache local
                  refreshState.current.mediaData.set(data.id, {
                    display_time: data.display_time,
                    selected_screens: data.selected_screens,
                    title: data.title
                  });

                  triggerRefresh(
                    `API: Mídia atualizada via ${method}`,
                    data.id,
                    data.display_time
                  );
                }
              } else {
                console.log('⏳ Update ignorado (debounce)');
              }
            }
          }
        }
      } catch (e) {
        // Erros na interceptação não devem afetar a request original
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [playerIsActivated, screenId, triggerRefresh]);

  // Expor funções globais para debug
  useEffect(() => {
    // Declarar interface para estender Window
    interface CustomWindow extends Window {
      __manualRefresh?: () => void;
      __triggerRefresh?: (reason: string, mediaId?: string | null, newTime?: number | null) => void;
      __stopDatabaseMonitor?: () => void;
      __cacheStatus?: () => Promise<void>;
      __clearCache?: () => Promise<void>;
      __cacheStats?: () => Promise<void>;
    }

    // Converter window para interface customizada
    const customWindow = window as CustomWindow;

    // Atribuir funções
    customWindow.__manualRefresh = () => triggerRefresh('Manual');
    customWindow.__triggerRefresh = triggerRefresh;
    customWindow.__stopDatabaseMonitor = () => {
      if (refreshState.current.pollingInterval) clearInterval(refreshState.current.pollingInterval);
      if (refreshState.current.channel) supabase.removeChannel(refreshState.current.channel);
      refreshState.current.inProgress = false;
      console.log('🛑 Monitor de banco PARADO');
    };

    // Funções de debug removidas para limpar console

    customWindow.__cacheStats = async () => {
      try {
        const cachedMedias = await getAllMediasFromCache();
        let totalSize = 0;
        for (const media of cachedMedias) {
          totalSize += media.blob.size;
        }
        console.log('📈 [CACHE DEBUG] Estatísticas:', {
          totalMidias: cachedMedias.length,
          tamanhoTotal: `${(totalSize / 1024 / 1024).toFixed(2)} MB`,
          mediasPorTipo: cachedMedias.reduce((acc, m) => {
            acc[m.format || 'unknown'] = (acc[m.format || 'unknown'] || 0) + 1;
            return acc;
          }, {} as Record<string, number>)
        });
      } catch (error) {
        console.error('❌ [CACHE DEBUG] Erro:', error);
      }
    };

    // Cleanup
    return () => {
      if (refreshState.current.pollingInterval) clearInterval(refreshState.current.pollingInterval);
      if (refreshState.current.channel) supabase.removeChannel(refreshState.current.channel);

      // Limpar funções globais
      delete customWindow.__manualRefresh;
      delete customWindow.__triggerRefresh;
      delete customWindow.__stopDatabaseMonitor;
      delete customWindow.__cacheStatus;
      delete customWindow.__clearCache;
      delete customWindow.__cacheStats;
    };
  }, [triggerRefresh]); // Adicionar triggerRefresh como dependência
}

// (Opcional) Polling backup para produção - descomente para ativar
/*
useEffect(() => {
  let lastDisplayTimes = {};
  let intervalId = setInterval(async () => {
    // Substitua por sua lógica de fetch de mídias
    const { data: medias } = await supabase.from('medias').select('id,display_time,selected_screens');
    if (medias && Array.isArray(medias)) {
      medias.forEach(media => {
        if (!lastDisplayTimes[media.id]) lastDisplayTimes[media.id] = media.display_time;
        if (lastDisplayTimes[media.id] !== media.display_time) {
          console.log('[POLLING] Mudança detectada via polling para mídia', media.id);
          lastDisplayTimes[media.id] = media.display_time;
          executeRefresh();
        }
      });
    }
  }, 30000); // 30 segundos
  return () => clearInterval(intervalId);
}, []);
*/




function Player() {
  const { nativeData, isNative } = useNativeBridge();

  // PASSO 1.1 - Adicionar debug inicial
  useEffect(() => {
    console.log('🔍 Player carregou');
    console.log('🔍 window.ReactNativeWebView existe?', !!(window as any).ReactNativeWebView);

    // Enviar mensagem de teste a cada 3 segundos (TEMPORÁRIO - para debug)
    const testInterval = setInterval(() => {
      console.log('📤 Tentando enviar PING...');

      if ((window as any).ReactNativeWebView) {
        console.log('✅ Enviando PING para WebView');
        (window as any).ReactNativeWebView.postMessage('PING_' + Date.now());
      } else {
        console.log('❌ ReactNativeWebView NÃO existe (rodando no navegador)');
      }
    }, 3000);

    return () => clearInterval(testInterval);
  }, []);

  useEffect(() => {
    if (nativeData) {
      console.log("Bateria:", nativeData.battery?.level);
      console.log("GPS:", nativeData.location);

      // Lógica Anti-Fraude Exemplo
      if (nativeData.battery && !nativeData.battery.charging && nativeData.battery.level < 0.2) {
        alert("Atenção: Dispositivo desconectado da energia!");
      }
    }
  }, [nativeData]);

  // 1.2 - Adicionar log inicial (debug)
  useEffect(() => {
    console.log('✅ WebApp carregou');

    // Notificar que carregou
    if ((window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(JSON.stringify({
        type: 'LOADED',
        deviceId: localStorage.getItem('deviceId') || localStorage.getItem('deviceCode'),
        timestamp: new Date().toISOString()
      }));
    }
  }, []);
  // =============================================================================
  // MODO PREVIEW: Detectar se está sendo aberto como preview do dashboard
  // Quando user_id vem na URL, é um preview e deve usar esse userId específico
  // NO MODO PREVIEW: 
  // - NÃO usa cache local (IndexedDB)
  // - Busca mídias diretamente das URLs originais
  // - Garante que sempre mostra as mídias do usuário logado no dashboard
  // =============================================================================
  const [isPreviewMode] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return !!urlParams.get('user_id');
  });

  const [previewUserId] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const userIdFromUrl = urlParams.get('user_id');
    if (userIdFromUrl) {
      console.log('🎬 [PLAYER PREVIEW] Modo preview detectado! userId da URL:', userIdFromUrl);
      console.log('🎬 [PLAYER PREVIEW] Cache local DESABILITADO - usando URLs originais');
      // Salvar temporariamente para que useMedias use o userId correto
      // Usar uma key separada para não interferir com o player real
      sessionStorage.setItem('player_preview_user_id', userIdFromUrl);
      return userIdFromUrl;
    }
    // Se não tem user_id na URL, limpar qualquer preview anterior
    sessionStorage.removeItem('player_preview_user_id');
    return null;
  });

  // Inicializar deviceCode a partir do localStorage/URL primeiro
  // Garante que deviceCode está correto e atualizado
  const [deviceCode, setDeviceCode] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Tentar obter código do parametro 'code' ou 'refresh' (fallback para casos de erro na URL)
    let urlCode = urlParams.get('code');
    if (!urlCode) {
      const refreshParam = urlParams.get('refresh');
      // Verifica se o parametro refresh parece um código (8 caracteres alfanuméricos)
      if (refreshParam && refreshParam.replace(/[^a-zA-Z0-9]/g, '').length === 8) {
        console.log('⚠️ [PLAYER] Usando parâmetro refresh como código:', refreshParam);
        urlCode = refreshParam;
      }
    }

    if (urlCode) {
      // No modo preview, NÃO salvar no localStorage para não interferir com player real
      if (!urlParams.get('user_id')) {
        localStorage.setItem('deviceCode', urlCode);
      }
      return urlCode.includes('-') ? urlCode : formatDeviceCode(urlCode);
    }
    let code = localStorage.getItem('deviceCode');
    if (!code) {
      code = generateDeviceCode();
      localStorage.setItem('deviceCode', code);
    }
    return code.includes('-') ? code : formatDeviceCode(code);
  });

  // Atualiza deviceCode se código da URL mudar
  // Atualiza deviceCode se código da URL mudar
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let urlCode = urlParams.get('code');

    if (!urlCode) {
      const refreshParam = urlParams.get('refresh');
      if (refreshParam && refreshParam.replace(/[^a-zA-Z0-9]/g, '').length === 8) {
        urlCode = refreshParam;
      }
    }

    // No modo preview, não atualizar localStorage
    if (urlCode) {
      const formatted = urlCode.includes('-') ? urlCode : formatDeviceCode(urlCode);
      if (formatted !== deviceCode) {
        console.log('🔄 [PLAYER] Atualizando código baseado na URL:', formatted);
        setDeviceCode(formatted);
        if (!previewUserId) {
          localStorage.setItem('deviceCode', urlCode);
        }
      }
    }
  }, [previewUserId, deviceCode]);

  // Hook para escutar eventos de atualização de display_time e estado do player
  const {
    filteredMedias: playerMedias,
    currentMediaIndex: playerCurrentMediaIndex,
    setCurrentMediaIndex: setPlayerCurrentMediaIndex,
    isActivated: playerIsActivated,
    deviceCode: playerDeviceCode,
    deviceId: playerDeviceId,
    deviceRecordId: playerDeviceRecordId, // UUID do dispositivo na tabela devices (para comandos remotos)
    hasActiveSubscription: playerHasActiveSubscription,
    activatedAccount: playerActivatedAccount,
    activatedUserId: playerActivatedUserId, // Adicionado para shutdown system
    selectedScreen: playerScreenId,
    refetchMedias, // Adicionar refetchMedias para refresh inteligente
    // organizationId removido pois não existe em UsePlayerReturn
    // ... outros valores do usePlayer
  } = usePlayer(deviceCode);

  // PASSO 1.3 - Adicionar notificação quando mídias iniciarem
  useEffect(() => {
    if (playerIsActivated) {
      console.log('🎬 MÍDIAS ATIVADAS!');
      console.log('🎬 Notificando WebView...');

      // Mudar título da página
      document.title = 'Player Ativo - ' + Date.now();

      // Notificar React Native WebView
      if ((window as any).ReactNativeWebView) {
        console.log('✅ Enviando MEDIA_STARTED');
        (window as any).ReactNativeWebView.postMessage('MEDIA_STARTED');
      } else {
        console.log('⚠️ ReactNativeWebView não disponível');
      }
    }
  }, [playerIsActivated]);

  // Lógica de Persistência e Comunicação Offline e Service Worker
  useEffect(() => {
    // 1. Registra o Service Worker (sw.js)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js');
    }

    // --- LÓGICA DA PONTE WATCH ---
    const KEY = 'watch_active_status';

    const checkAndNotify = () => {
      const params = new URLSearchParams(window.location.search);

      // 1. Verifica se a URL atual diz que está ativado
      const urlIsActive = params.get('activated') === 'true';

      // 2. Se sim, salva no LocalStorage para o futuro
      if (urlIsActive) {
        localStorage.setItem(KEY, 'true');
      }

      // 3. Verifica o estado final (URL ou Cache)
      const isActive = localStorage.getItem(KEY) === 'true';

      const status = {
        type: 'WATCH_STATUS',
        activated: isActive,
        url: window.location.href,
        connection: navigator.onLine ? 'online' : 'offline'
      };

      // 4. Envia para o App Nativo (React Native)
      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(JSON.stringify(status));
      }
    };

    // Executa ao montar o componente
    checkAndNotify();

    // Opcional: Monitorar mudanças de URL se você usa React Router
    window.addEventListener('popstate', checkAndNotify);

    return () => window.removeEventListener('popstate', checkAndNotify);
  }, []);

  // PASSO 1.4 - Atualizar URL visualmente para o WebView nativo detectar mudanças
  useEffect(() => {
    if (!deviceCode) return;

    try {
      const url = new URL(window.location.href);
      const currentCode = url.searchParams.get('code');
      const currentActivated = url.searchParams.get('activated');
      const newActivated = playerIsActivated ? 'true' : 'false';

      // Apenas atualizar se houver mudança para evitar loops ou overhead
      if (currentCode !== deviceCode || currentActivated !== newActivated) {
        url.searchParams.set('code', deviceCode);
        url.searchParams.set('activated', newActivated);

        window.history.replaceState({}, '', url.toString());
        console.log(`🌐 [PLAYER] URL Atualizada para Debug/WebView: ?code=${deviceCode}&activated=${newActivated}`);
      }
    } catch (e) {
      console.error('Erro ao atualizar URL visual:', e);
    }
  }, [deviceCode, playerIsActivated]);

  // =============================================================================
  // COMANDOS REMOTOS - Escuta comandos do dashboard (ex: Reset Storage)
  // Usa deviceRecordId que é o UUID do dispositivo na tabela devices
  // =============================================================================
  useRemoteCommands({ deviceId: playerDeviceRecordId });

  // Event listener para refresh automático em tempo real do Dashboard
  useEffect(() => {
    if (!playerIsActivated || !playerScreenId) return;

    const handleDashboardRefresh = async (event: CustomEvent) => {
      console.log('🔄 [PLAYER] Evento dashboardRefreshAllPlayers recebido:', event.detail);

      try {
        // Refresh inteligente sem recarregar a página
        if (refetchMedias) {
          console.log('📡 [PLAYER] Executando refresh inteligente das mídias...');
          await refetchMedias();
          console.log('✅ [PLAYER] Refresh inteligente concluído');
        }
      } catch (error) {
        console.error('❌ [PLAYER] Erro no refresh automático:', error);
        // Fallback para refresh completo apenas em caso de erro
        window.location.reload();
      }
    };

    // Handler para eventos de sincronização em tempo real
    const handleRealtimeSync = async (event: CustomEvent) => {
      console.log(' [PLAYER] Evento de sincronização em tempo real RECEBIDO:', event.detail);
      console.log('📨 [PLAYER] Estado atual - isActivated:', playerIsActivated, 'screenId:', playerScreenId);

      try {
        // Refresh inteligente das mídias quando detectar mudanças no banco
        if (refetchMedias) {
          console.log('📡 [PLAYER] Executando refresh por sincronização em tempo real...');
          await refetchMedias();
          console.log('✅ [PLAYER] Refresh por sincronização em tempo real concluído');
        } else {
          console.log('⚠️ [PLAYER] refetchMedias não disponível');
        }
      } catch (error) {
        console.error('❌ [PLAYER] Erro no refresh por sincronização em tempo real:', error);
      }
    };

    // Handler para quando uma nova mídia é criada
    const handleNewMediaCreated = async (event: CustomEvent) => {
      console.log('🎬 [PLAYER] Nova mídia criada detectada:', event.detail);

      const { media } = event.detail;

      // Verificar se a mídia é relevante para este player
      if (media && media.selected_screens && Array.isArray(media.selected_screens)) {
        const isRelevantToThisPlayer = media.selected_screens.length === 0 ||
          media.selected_screens.includes(playerScreenId);

        console.log('🎯 [PLAYER] Verificando relevância da mídia:', {
          mediaTitle: media.title,
          mediaScreens: media.selected_screens,
          playerScreenId,
          isRelevant: isRelevantToThisPlayer
        });

        if (isRelevantToThisPlayer) {
          console.log('✅ [PLAYER] Mídia relevante para este player, atualizando...');

          try {
            if (refetchMedias) {
              await refetchMedias();
              console.log('✅ [PLAYER] Mídias atualizadas com sucesso após criação');
            }
          } catch (error) {
            console.error('❌ [PLAYER] Erro ao atualizar mídias após criação:', error);
          }
        } else {
          console.log('⏭️ [PLAYER] Mídia não relevante para este player, ignorando');
        }
      }
    };

    // Handler para quando a ordem das mídias é alterada no dashboard
    const handleMediaOrderUpdated = async (event: CustomEvent) => {
      console.log('🔄 [PLAYER] Ordem de mídias atualizada detectada:', event.detail);

      try {
        if (refetchMedias) {
          console.log('📡 [PLAYER] Executando refresh por mudança de ordem...');
          await refetchMedias();
          console.log('✅ [PLAYER] Mídias reordenadas com sucesso');
        }
      } catch (error) {
        console.error('❌ [PLAYER] Erro ao atualizar mídias após reordenação:', error);
      }
    };

    // Adicionar listeners para eventos
    window.addEventListener('dashboardRefreshAllPlayers', handleDashboardRefresh as EventListener);
    window.addEventListener('realtime-sync-requested', handleRealtimeSync as EventListener);
    window.addEventListener('newMediaCreated', handleNewMediaCreated as EventListener);
    window.addEventListener('dashboardDataUpdate', handleMediaOrderUpdated as EventListener);

    console.log('🎯 [PLAYER] Listener dashboardRefreshAllPlayers ativado para screenId:', playerScreenId);

    return () => {
      window.removeEventListener('dashboardRefreshAllPlayers', handleDashboardRefresh as EventListener);
      window.removeEventListener('realtime-sync-requested', handleRealtimeSync as EventListener);
      window.removeEventListener('newMediaCreated', handleNewMediaCreated as EventListener);
      window.removeEventListener('dashboardDataUpdate', handleMediaOrderUpdated as EventListener);
      console.log('🔌 [PLAYER] Todos os listeners de eventos removidos');
    };
  }, [playerIsActivated, playerScreenId, refetchMedias]);

  // Ref para guardar o estado anterior das mídias (IDs e tempos)
  const prevMediasRef = useRef<Array<{ id: string, display_time?: number }>>([]);


  useEffect(() => {
    if (!playerIsActivated || !playerHasActiveSubscription) return;
    const prevMedias = prevMediasRef.current;
    const currentMedias = playerMedias.map(m => ({ id: m.id, display_time: m.display_time }));

    // Detectar adição/remoção
    const prevIds = prevMedias.map(m => m.id);
    const currentIds = currentMedias.map(m => m.id);
    const added = currentIds.filter(id => !prevIds.includes(id));
    const removed = prevIds.filter(id => !currentIds.includes(id));

    // Detectar alteração de tempo de exibição
    let displayTimeChanged = false;
    for (const curr of currentMedias) {
      const prev = prevMedias.find(m => m.id === curr.id);
      if (prev && prev.display_time !== curr.display_time) {
        displayTimeChanged = true;
        break;
      }
    }

    // Usar marcador para evitar loop de refresh
    const refreshMarker = 'player_refresh_marker';
    const lastRefresh = sessionStorage.getItem(refreshMarker);
    const now = Date.now();

    // Proteção extra: só refresca se não acabou de refrescar (10s de proteção)
    if ((added.length > 0 || removed.length > 0 || displayTimeChanged) && (!lastRefresh || now - Number(lastRefresh) > 10000)) {
      console.log('[PLAYER][REFRESH] Mudança detectada nas mídias: adição:', added, 'remoção:', removed, 'display_time alterado:', displayTimeChanged);
      sessionStorage.setItem(refreshMarker, String(now));
      // Em vez de reload, apenas refetch as mídias se possível
      if (typeof refetchMedias === 'function') {
        refetchMedias();
      } else {
        // Fallback: reload apenas se realmente necessário
        setTimeout(() => window.location.reload(), 500);
      }
    }

    // Atualizar ref
    prevMediasRef.current = currentMedias;
  }, [playerMedias, playerIsActivated, playerHasActiveSubscription, refetchMedias]);

  // =============================================================================
  // VERIFICAÇÃO DE MUDANÇA DE USUÁRIO - Limpa cache ao trocar de conta
  // Garante que cada usuário veja apenas suas próprias mídias
  // =============================================================================
  useEffect(() => {
    const currentUserId = sessionStorage.getItem('player_activated_user_id');
    const cachedUserId = localStorage.getItem('player_cache_user_id');

    if (currentUserId && cachedUserId && currentUserId !== cachedUserId) {
      console.log('🔄 [PLAYER] Usuário mudou! Limpando cache de mídias...');
      console.log('🔄 [PLAYER] Usuário anterior:', cachedUserId);
      console.log('🔄 [PLAYER] Usuário atual:', currentUserId);

      // Limpar o IndexedDB (cache de mídias)
      if ('indexedDB' in window) {
        try {
          indexedDB.deleteDatabase('watch-media-cache-v2');
          console.log('✅ [PLAYER] Cache IndexedDB limpo com sucesso');
        } catch (e) {
          console.error('❌ [PLAYER] Erro ao limpar cache IndexedDB:', e);
        }
      }

      // Atualizar o userId do cache
      localStorage.setItem('player_cache_user_id', currentUserId);

      // Recarregar a página para garantir que as mídias corretas sejam carregadas
      console.log('🔄 [PLAYER] Recarregando página para carregar mídias do novo usuário...');
      window.location.reload();
      return;
    }

    // Se é a primeira vez ou o mesmo usuário, apenas salvar o userId
    if (currentUserId) {
      localStorage.setItem('player_cache_user_id', currentUserId);
    }
  }, [playerActivatedAccount]);

  // =============================================================================
  // SISTEMA DE CACHE PERSISTENTE - Netflix-style Offline Storage
  // O hook useMediaCache gerencia automaticamente:
  // 1. Verificação se mídia existe no cache local (IndexedDB)
  // 2. Download e armazenamento de novas mídias
  // 3. Criação de URLs blob para reprodução offline
  // 4. Limpeza automática de mídias antigas
  // 
  // MODO PREVIEW: Cache é desabilitado para garantir exibição das URLs originais
  // =============================================================================
  const {
    mediasWithCache,
    cacheStatus,
    refreshCache,
    clearAllCache,
    getCacheInfo
  } = useMediaCache(playerMedias, isPreviewMode); // isPreviewMode desabilita cache

  // Log de status do cache para debug
  useEffect(() => {
    if (cacheStatus.isInitialized) {
      console.log('📊 [CACHE] Status do cache persistente Netflix-style:', {
        suportado: cacheStatus.isSupported,
        persistente: cacheStatus.isPersistent ? '✅ SIM - Dados protegidos!' : '⚠️ NÃO - Navegador pode limpar',
        totalCacheado: `${cacheStatus.totalCachedMB}MB`,
        qtdMidias: cacheStatus.totalCachedCount,
        processando: cacheStatus.isProcessing,
        progresso: `${cacheStatus.processingProgress}%`,
        armazenamento: {
          quota: cacheStatus.storageQuota || 'N/A',
          uso: cacheStatus.storageUsage || 'N/A',
          disponivel: cacheStatus.storageAvailable || 'N/A'
        }
      });
    }
  }, [cacheStatus]);

  // Expor funções de cache para debug no console
  useEffect(() => {
    interface CustomWindow extends Window {
      __cacheStatus?: () => Promise<void>;
      __clearCache?: () => Promise<void>;
      __refreshCache?: () => Promise<void>;
    }
    const customWindow = window as CustomWindow;

    customWindow.__cacheStatus = async () => {
      const info = await getCacheInfo();
      console.log('📊 [CACHE DEBUG] Estatísticas:', info);
    };

    customWindow.__clearCache = async () => {
      await clearAllCache();
      console.log('🧹 [CACHE DEBUG] Cache limpo com sucesso');
    };

    customWindow.__refreshCache = async () => {
      await refreshCache();
      console.log('🔄 [CACHE DEBUG] Cache atualizado');
    };

    return () => {
      delete customWindow.__cacheStatus;
      delete customWindow.__clearCache;
      delete customWindow.__refreshCache;
    };
  }, [getCacheInfo, clearAllCache, refreshCache]);

  // =============================================================================
  // SISTEMA DE DESLIGAMENTO AUTOMÁTICO (SHUTDOWN/ON-OFF)
  // =============================================================================
  const [shutdownSchedule, setShutdownSchedule] = useState({});
  const [isShutdownSystemActive, setIsShutdownSystemActive] = useState(false);
  const [isPlayerManuallyOn, setIsPlayerManuallyOn] = useState(true); // toggle manual

  useEffect(() => {
    // Definir ID do usuário (do player ativado ou do preview)
    const userId = previewUserId || playerActivatedUserId;

    console.log('🔌 [SHUTDOWN PLAYER] Verificando userId para carregar schedule:', {
      previewUserId,
      playerActivatedUserId,
      userId
    });

    if (!userId) {
      console.log('⚠️ [SHUTDOWN PLAYER] Sem userId, não carregando schedule');
      return;
    }

    // Variável para armazenar o último valor conhecido (para log apenas)
    let lastLoggedValue: boolean | null = null;

    // Função para carregar configurações
    const loadSchedule = async () => {
      try {
        const { data, error } = await supabase
          .from('user_shutdown_schedules')
          .select('*')
          .eq('user_id', userId)
          .single();

        if (error) {
          // Sem registro = padrão LIGADO
          setIsPlayerManuallyOn(true);
          setIsShutdownSystemActive(false);
          return;
        }

        if (data) {
          // Usar valor exato do banco: se is_manually_on é false = desligado, se true/null/undefined = ligado
          const rawValue = data.is_manually_on;
          const newManuallyOn = rawValue === false ? false : true;

          // Log apenas quando valor muda (para não poluir console)
          if (lastLoggedValue !== newManuallyOn) {
            console.log('🔌 [SHUTDOWN PLAYER] Estado manual ALTERADO:', {
              anterior: lastLoggedValue,
              novo: newManuallyOn,
              is_manually_on_raw: rawValue,
              acao: newManuallyOn ? 'LIGANDO (exibir mídias)' : 'DESLIGANDO (tela preta)'
            });
            lastLoggedValue = newManuallyOn;
          }

          // Log do agendamento automático
          console.log('🕐 [SHUTDOWN PLAYER] Configuração de agendamento:', {
            is_active: data.is_active,
            schedule_config: data.schedule_config,
            diasConfigurados: data.schedule_config ? Object.keys(data.schedule_config).filter(
              day => data.schedule_config[day]?.enabled
            ) : []
          });

          // SEMPRE atualizar o estado para garantir sincronização
          setIsPlayerManuallyOn(newManuallyOn);
          setIsShutdownSystemActive(data.is_active || false);
          setShutdownSchedule(data.schedule_config || {});
        }
      } catch (e) {
        console.log('❌ [SHUTDOWN PLAYER] Erro ao carregar schedule:', e);
      }
    };

    // Carregar imediatamente
    loadSchedule();

    // POLLING: Verificar a cada 3 segundos (fallback para Realtime)
    const pollingInterval = setInterval(() => {
      loadSchedule();
    }, 3000);

    // Subscribe para mudanças em tempo real (pode não funcionar se Realtime não estiver habilitado)
    const channel = supabase
      .channel(`shutdown_schedule_${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_shutdown_schedules',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          console.log('🔌 [PLAYER SHUTDOWN] Mudança detectada via Realtime:', payload);
          console.log('🔌 [PLAYER SHUTDOWN] Novo valor is_manually_on:', payload.new?.is_manually_on);
          loadSchedule();
        }
      )
      .subscribe((status) => {
        console.log(`🔌 [PLAYER SHUTDOWN] Status do canal Realtime: ${status}`);
        if (status === 'SUBSCRIBED') {
          console.log(`✅ [PLAYER SHUTDOWN] Canal Realtime conectado para userId: ${userId}`);
        }
      });

    return () => {
      clearInterval(pollingInterval);
      supabase.removeChannel(channel);
    };
  }, [previewUserId, playerActivatedUserId]);

  const location = useLocation();



  // Extrair e salvar código da URL
  const urlParams = new URLSearchParams(location.search);
  const codeFromUrl = urlParams.get('code');

  useEffect(() => {
    if (codeFromUrl) {
      const formattedCode = formatDeviceCode(codeFromUrl);
      console.log('📱 Código da URL:', formattedCode);
      sessionStorage.setItem('player_device_code', formattedCode);
      localStorage.setItem('deviceCode', formattedCode);
    }
  }, [codeFromUrl]);

  // Função para obter código do dispositivo - agora apenas retorna o que já está no localStorage
  function getDeviceCodeFromQuery() {
    // O código da URL já foi processado no useEffect acima
    // Agora apenas retornamos o que está no localStorage
    const storedCode = localStorage.getItem('deviceCode');
    if (storedCode) {
      console.log('📱 Código encontrado no localStorage:', storedCode);
      return storedCode;
    }

    // Se não houver código no localStorage, retornar null
    // para que o useDeviceActivation possa gerar um novo
    console.log('📱 Nenhum código encontrado no localStorage, retornando null');
    return null;
  }
  const [showSplash, setShowSplash] = useState(false);
  // Remove estados locais redundantes, usa apenas valores do usePlayer
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  // Removido: const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);
  // Notificação para adição/remoção de mídia
  const [mediaChangeNotification, setMediaChangeNotification] = useState<string | null>(null);

  // Estado para controlar a busca de mídias
  const [isLoadingMedias, setIsLoadingMedias] = useState(false);

  // Estado para controlar notificações de novas mídias
  const [lastMediaCount, setLastMediaCount] = useState(0);

  // Estado para controlar o brilho da tela
  const [brightness, setBrightness] = useState(() => {
    const savedBrightness = localStorage.getItem('player_brightness');
    return savedBrightness ? Number(savedBrightness) : 100;
  });
  const hasShownSplashRef = useRef(false);
  const splashDismissedRef = useRef(false);

  // Efeito para salvar o brilho no localStorage
  useEffect(() => {
    localStorage.setItem('player_brightness', String(brightness));
  }, [brightness]);

  // Refs para controlar estados anteriores e evitar loops
  const prevScreenIdRef = useRef<string>('');
  const prevIsActivatedRef = useRef<boolean>(false);
  const prevMediasLengthRef = useRef<number>(0);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  // Esconder notificação de adição/remoção após alguns segundos
  useEffect(() => {
    if (mediaChangeNotification) {
      const timer = setTimeout(() => setMediaChangeNotification(null), 3500);
      return () => clearTimeout(timer);
    }
  }, [mediaChangeNotification]);

  // Buscar nome da tela associada ao ativar o player
  const [screens, setScreens] = useState<{ id: string, name: string }[]>([]);
  useEffect(() => {
    async function fetchScreenName() {
      if (playerScreenId) {
        const { data, error } = await supabase
          .from('screens')
          .select('id, name')
          .eq('id', playerScreenId)
          .single();
        if (data && data.id && data.name) {
          setScreens([{ id: data.id, name: data.name }]);
        } else {
          setScreens([]);
        }
      } else {
        setScreens([]);
      }
    }
    fetchScreenName();
  }, [playerScreenId]);

  // Efeito para remover o overflow do body
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);


  // Função para filtrar mídias por agendamento
  // Remover a função filterMediasBySchedule e o estado filteredMedias
  // const filterMediasBySchedule = (allMedias: Media[]): void => { ... };




  // Ref para lista anterior de IDs de mídias (deve vir após o hook usePlayer)
  const prevMediaIdsRef = useRef<string[]>([]);

  // Efeito para detectar adição/remoção de mídias e mostrar notificação
  useEffect(() => {
    if (!playerMedias) return;
    const prevIds = prevMediaIdsRef.current;
    const currentIds = playerMedias.map(m => m.id);
    // Detectar adições
    const added = currentIds.filter(id => !prevIds.includes(id));
    // Detectar remoções
    const removed = prevIds.filter(id => !currentIds.includes(id));
    prevMediaIdsRef.current = currentIds;
    // Aqui você pode disparar notificações se quiser
  }, [playerMedias]);
  // Monitoramento robusto de mudanças no banco de dados (Polling + Realtime + interceptação)
  useDatabaseMonitor({ screenId: playerScreenId, playerIsActivated });



  // Efeito para controlar o splash screen e inicialização do player
  useEffect(() => {
    console.log('[SPLASH] Controlando splash screen:', {
      playerIsActivated,
      playerHasActiveSubscription,
      showSplash
    });

    if (!playerIsActivated || !playerHasActiveSubscription) {
      setShowSplash(false);
      setIsPlayerReady(false);
      return;
    }

    if (!hasShownSplashRef.current) {
      setShowSplash(true);
      setIsPlayerReady(true);
      hasShownSplashRef.current = true;
    }
  }, [playerIsActivated, playerHasActiveSubscription]);

  useEffect(() => {
    if (showSplash && playerMedias.length > 0 && !splashDismissedRef.current) {
      setShowSplash(false);
      setIsPlayerReady(true);
      splashDismissedRef.current = true;
    }
  }, [showSplash, playerMedias.length]);

  useEffect(() => {
    const changed = localStorage.getItem('displayTimeChanged');
    if (changed) {
      try {
        const { mediaId, newTime } = JSON.parse(changed);
        const match = playerMedias.some(m => m.id === mediaId && m.display_time === newTime);
        if (match) {
          localStorage.removeItem('displayTimeChanged');
        }
      } catch {
        localStorage.removeItem('displayTimeChanged');
      }
    }
  }, [playerMedias]);

  // Hook para navegação automática entre mídias
  useMediaNavigation(playerMedias, playerCurrentMediaIndex, setPlayerCurrentMediaIndex);

  // Remover o useEffect que chamava filterMediasBySchedule
  // useEffect(() => {
  //   filterMediasBySchedule(medias);
  //   const scheduleTimer = setInterval(() => {
  //     filterMediasBySchedule(medias);
  //   }, 300000);
  //   return () => clearInterval(scheduleTimer);
  // }, [medias]);

  // Efeito para monitorar mudanças nas mídias filtradas
  useEffect(() => {
    console.log('[PLAYER] 📺 Mídias filtradas atualizadas:', {
      total: playerMedias.length,
      mídias: playerMedias.map(m => m.title),
      índiceAtual: playerCurrentMediaIndex,
      isActivated: playerIsActivated,
      hasActiveSubscription: playerHasActiveSubscription,
      deviceCode: playerDeviceCode
    });
  }, [playerMedias, playerCurrentMediaIndex, playerIsActivated, playerHasActiveSubscription, playerDeviceCode]);

  // Efeito otimizado para detectar mudanças nas mídias (sem notificações visuais)
  useEffect(() => {
    if (lastMediaCount !== playerMedias.length) {
      const diff = playerMedias.length - lastMediaCount;
      if (diff > 0) {
        console.log(`[PLAYER] 📈 ${diff} nova(s) mídia(s) detectada(s)`);
      } else if (diff < 0) {
        console.log(`[PLAYER] 📉 ${Math.abs(diff)} mídia(s) removida(s)`);
      }
      setLastMediaCount(playerMedias.length);
    }
  }, [playerMedias.length, lastMediaCount]);

  // ... rest of the effects ...

  // =============================================================================
  // MÍDIAS COM CACHE - Comportamento condicional
  // 
  // MODO PREVIEW (Dashboard): 
  //   - SEMPRE usa URLs originais diretamente
  //   - NÃO usa cache local (IndexedDB)
  //   - Garante mídias corretas do usuário logado
  //
  // MODO NORMAL (URL direta /player):
  //   - Pode usar cache local para reprodução offline
  //   - Baixa mídias para armazenamento local
  // =============================================================================

  // No modo preview, SEMPRE usar URLs originais (sem cache)
  // No modo normal, usar playerMedias (cache temporariamente desabilitado)
  const mediasToDisplay = isPreviewMode ? playerMedias : playerMedias;

  // DEBUG: Log para diagnosticar problema de exibição
  useEffect(() => {
    console.log('🎬 [PLAYER DEBUG] Estado das mídias:', {
      isPreviewMode,
      previewUserId,
      playerMediasCount: playerMedias.length,
      mediasWithCacheCount: mediasWithCache.length,
      mediasToDisplayCount: mediasToDisplay.length,
      usingCache: !isPreviewMode && false, // Cache desabilitado por enquanto, exceto preview que NUNCA usa
      modoOperacao: isPreviewMode ? '🎬 PREVIEW (URLs originais sempre)' : '📺 NORMAL (cache quando habilitado)',
      firstMedia: mediasToDisplay[0] ? {
        id: mediasToDisplay[0].id,
        title: mediasToDisplay[0].title,
        url: mediasToDisplay[0].url?.substring(0, 80),
        type: mediasToDisplay[0].type
      } : null
    });
  }, [playerMedias, mediasWithCache, mediasToDisplay, isPreviewMode, previewUserId]);

  // Usar URLs originais diretamente
  // No modo PREVIEW: sempre URLs originais para garantir mídias corretas
  // No modo NORMAL: URLs originais por enquanto (cache desabilitado temporariamente)
  const mediasWithLocalUrl = mediasToDisplay.map(m => ({
    ...m,
    // Usar URL original diretamente
    url: m.url,
    // Preservar o tipo original da mídia para detecção correta
    originalType: m.type
  }));

  // Lógica de Previsão do Tempo (Weather Overlay)
  const [weatherConfig, setWeatherConfig] = useState<{ enabled: boolean, interval: number, duration: number, location?: string } | null>(null);
  const [showWeatherOverlay, setShowWeatherOverlay] = useState(false);

  // Carregar e sincronizar config de clima - AGORA BUSCA DO BANCO DE DADOS
  useEffect(() => {
    if (!playerDeviceCode) return;

    // Função para buscar configuração do banco de dados (Supabase)
    const loadConfigFromDatabase = async () => {
      try {
        console.log('🌤️ [WEATHER] Buscando weather_config do banco para:', playerDeviceCode);

        const { data: deviceData, error } = await supabase
          .from('devices')
          .select('weather_config')
          .eq('code', playerDeviceCode)
          .maybeSingle();

        if (error) {
          console.log('⚠️ [WEATHER] Erro ao buscar do banco:', error.message);
          return;
        }

        if (deviceData?.weather_config) {
          console.log('✅ [WEATHER] Config carregada do banco:', deviceData.weather_config);
          setWeatherConfig(deviceData.weather_config);
        } else {
          console.log('ℹ️ [WEATHER] Nenhuma config encontrada no banco, usando padrão');
          setWeatherConfig({ enabled: false, interval: 15, duration: 15 });
        }
      } catch (err) {
        console.error('[PLAYER] Erro ao buscar weather config do banco:', err);
      }
    };

    // Função para carregar do localStorage (fallback/cache local)
    const loadConfigFromLocalStorage = () => {
      const activationKey = `device_activation_${playerDeviceCode}`;
      const dataStr = localStorage.getItem(activationKey);
      if (dataStr) {
        try {
          const data = JSON.parse(dataStr);
          if (data.weather_config) {
            setWeatherConfig(data.weather_config);
            return true;
          }
        } catch (e) {
          console.error('[PLAYER] Erro ao ler weather config do localStorage:', e);
        }
      }
      return false;
    };

    // Primeiro tentar localStorage (rápido), depois buscar do banco (atualizado)
    loadConfigFromLocalStorage();
    loadConfigFromDatabase();

    const handleSync = (e: CustomEvent) => {
      if (e.detail?.weather_config) {
        console.log('✅ [PLAYER] Weather Config sincronizado via evento:', e.detail.weather_config);
        setWeatherConfig(e.detail.weather_config);
      } else if (e.detail) {
        // Se recebeu evento sem weather_config, recarregar do banco
        loadConfigFromDatabase();
      }
    };

    const handleStorage = () => {
      loadConfigFromDatabase();
    };

    window.addEventListener('deviceUpdateSync', handleSync as EventListener);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('deviceUpdateSync', handleSync as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, [playerDeviceCode]);

  // Timer do Weather
  useEffect(() => {
    if (!weatherConfig?.enabled) {
      setShowWeatherOverlay(false);
      return;
    }

    // Intervalo em MS (mínimo 1 minuto)
    const intervalMs = Math.max(1, weatherConfig.interval) * 60 * 1000;

    // Função para mostrar o overlay
    const show = (durationOverride?: number) => {
      console.log('🌤️ [WEATHER] Mostrando overlay de clima...');
      setShowWeatherOverlay(true);
      const durationSec = durationOverride || Math.min(20, Math.max(1, weatherConfig.duration));
      setTimeout(() => {
        setShowWeatherOverlay(false);
      }, durationSec * 1000);
    };

    // Mostrar imediatamente para teste com duração estendida (60s) para leitura do debug
    setTimeout(() => show(60), 1000);

    const timer = setInterval(() => show(), intervalMs);

    return () => clearInterval(timer);
  }, [weatherConfig]);

  // 1.1 - Adicionar notificação quando mídias começarem a exibir
  const showMedia = playerIsActivated && playerHasActiveSubscription && !showSplash && playerMedias.length > 0;

  useEffect(() => {
    if (showMedia) { // OU qualquer variável que indica que mídias estão ativas
      console.log('🎬 Mídias ativadas - notificando WebView');

      // Notificar o React Native WebView
      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage('MEDIA_STARTED');
      }

      // Mudar título para confirmar (debug)
      document.title = 'Player Ativo';
    }
  }, [showMedia]);


  return (
    <div className="w-screen h-screen bg-black overflow-hidden">
      <ShutdownOverlay
        isActive={isShutdownSystemActive}
        schedule={shutdownSchedule}
        isPreviewMode={isPreviewMode}
        isManuallyOn={isPlayerManuallyOn}
        userId={previewUserId || playerActivatedUserId}
        onManualToggleSync={(newValue) => {
          console.log('🔄 [PLAYER] Toggle sincronizado pelo agendamento:', newValue);
          setIsPlayerManuallyOn(newValue);
        }}
      />
      {mediaChangeNotification && (
        <SuccessNotification message={mediaChangeNotification} onClose={() => setMediaChangeNotification(null)} />
      )}
      {!playerIsActivated ? (
        <PlayerActivationScreen
          deviceCode={playerDeviceCode}
          deviceId={playerDeviceId}
        />
      ) : !playerHasActiveSubscription ? (
        <div className="w-full h-full flex items-center justify-center bg-black text-white">
          <div className="text-center">
            <h2 className="text-2xl mb-4">Assinatura Necessária</h2>
            <p>Por favor, ative sua assinatura para continuar.</p>
          </div>
        </div>
      ) : showSplash ? (
        <SplashScreen
          onComplete={() => setShowSplash(false)}
          isLoadingMedias={true}
          hasMedias={playerMedias.length > 0}
          isActivated={playerIsActivated}
          hasActiveSubscription={playerHasActiveSubscription}
        />
      ) : playerMedias.length === 0 ? (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0A3622] to-[#071E14] text-white">
          <div className="text-center">
            {/* Logo com efeito de brilho */}
            <div className="w-40 h-40 mx-auto mb-8 relative">
              <div className="absolute inset-0 bg-[#39b92c] opacity-30 blur-2xl rounded-full animate-pulse"></div>
              <img
                src="https://locaninja.com/logotipo-watch-white.png"
                alt="Watch Logo"
                className="w-full h-full object-contain drop-shadow-[0_0_20px_rgba(57,185,44,0.5)] relative z-10"
              />
            </div>

            {/* Mensagem principal */}
            <h2 className="text-3xl font-bold text-white mb-3">Aguardando Mídia</h2>
            <p className="text-[#68C095] text-lg mb-6">As mídias serão exibidas automaticamente</p>

            {/* Indicador de loading sutil */}
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 bg-[#39b92c] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-[#39b92c] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-[#39b92c] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      ) : (
        <MediaDisplay
          isAuthenticated={playerIsActivated}
          filteredMedias={mediasWithLocalUrl}
          currentMediaIndex={playerCurrentMediaIndex}
          onPreviousMedia={() => {
            if (playerMedias.length <= 1) return;
            setPlayerCurrentMediaIndex(
              playerCurrentMediaIndex === 0 ? playerMedias.length - 1 : playerCurrentMediaIndex - 1
            );
          }}
          onNextMedia={() => {
            if (playerMedias.length <= 1) return;
            setPlayerCurrentMediaIndex(
              playerCurrentMediaIndex === playerMedias.length - 1 ? 0 : playerCurrentMediaIndex + 1
            );
          }}
          currentUser={playerActivatedAccount}
          deviceId={deviceCode}
          selectedScreen={playerScreenId}
          screens={screens}
          brightness={brightness}
          deviceCode={playerDeviceCode}
          isLoadingMedias={isLoadingMedias || cacheStatus.isProcessing}
          cachedMediasCount={cacheStatus.totalCachedCount}
        />
      )}

      {showWeatherOverlay && <WeatherOverlay locationOverride={weatherConfig?.location} />}
    </div>
  );
}

export default Player;
